import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Grid,
  InputAdornment,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarTodayIcon,
  Add as AddIcon,
  Save as SaveIcon,
  InfoOutlined as InfoIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import {
  DeploymentCreate,
  DeploymentUpdate,
  DeploymentAdminListDetail,
  ProjectAdminSummary,
  Site,
  DeviceAdminDetailWithDeployment,
  LocationAdminDetail,
  DeploymentStatus
} from 'src/api/Api';
import { useSnackbar, SnackbarType } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';
import { getProjectDeviceCustodyList } from 'src/api2/deviceCustodyApi';

interface DeploymentDialogProps {
  open: boolean;
  onClose: () => void;
  deployment?: DeploymentAdminListDetail | null; // If provided, we are in EDIT mode
  onSuccess: (deployment: DeploymentAdminListDetail) => void;
  // Optional pre-selections for Create mode
  initialProjectId?: string;
  initialSiteId?: string;
}

const DeploymentDialog: FC<DeploymentDialogProps> = ({
  open,
  onClose,
  deployment,
  onSuccess,
  initialProjectId,
  initialSiteId
}) => {
  const isEdit = !!deployment;
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const [projects, setProjects] = useState<ProjectAdminSummary[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [locations, setLocations] = useState<LocationAdminDetail[]>([]);
  const [devices, setDevices] = useState<DeviceAdminDetailWithDeployment[]>([]);

  const [formData, setFormData] = useState({
    deployment_name: '',
    project_id: '',
    site_id: '',
    location_id: '',
    device_id: '',
    start_date: '',
    end_date: '',
    data_upload_status: false,
    status: DeploymentStatus.Upcoming as DeploymentStatus,
  });

  const [errors, setErrors] = useState<Record<string, string | null>>({});

  useEffect(() => {
    if (open) {
      if (isEdit && deployment) {
        setFormData({
          deployment_name: deployment.deployment_name || '',
          project_id: (deployment as any).project_id || '',
          site_id: (deployment as any).site_id || '',
          location_id: deployment.location_id || '',
          device_id: deployment.device_id || '',
          start_date: deployment.start_date ? deployment.start_date.split('T')[0] : '',
          end_date: deployment.end_date ? deployment.end_date.split('T')[0] : '',
          data_upload_status: deployment.data_upload_status || false,
          status: (deployment.status as DeploymentStatus) || DeploymentStatus.Upcoming,
        });
        if ((deployment as any).project_id) fetchSites((deployment as any).project_id);
        if ((deployment as any).site_id) fetchLocations((deployment as any).site_id);
      } else {
        setFormData({
          deployment_name: '',
          project_id: initialProjectId || '',
          site_id: initialSiteId || '',
          location_id: '',
          device_id: '',
          start_date: '',
          end_date: '',
          data_upload_status: false,
          status: DeploymentStatus.Upcoming,
        });
        fetchInitialData();
        if (initialProjectId) fetchSites(initialProjectId);
        if (initialSiteId) fetchLocations(initialSiteId);
      }
      setErrors({});
    }
  }, [open, deployment, isEdit, initialProjectId, initialSiteId]);

  const fetchInitialData = async () => {
    try {
      const projectsResp = await clientService.api.getAllProjectsAdminApiV1AdminProjectsGet();
      setProjects(projectsResp.data);
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    }
  };
  useEffect(() => {
    const fetchProjectDevices = async () => {
      if (!formData.project_id) {
        setDevices([]);
        return;
      }
      try {
        const custodyRecords = await getProjectDeviceCustodyList(formData.project_id);
        const safeCustodyRecords = Array.isArray(custodyRecords) ? custodyRecords : [];
        const activeCustodies = safeCustodyRecords.filter(r => r && r.returned_at === null && r.device);
        
        // Map custody records to DeviceAdminDetailWithDeployment objects
        const projectDevices = activeCustodies.map(r => ({
          ...r.device!,
          deployment_id: undefined,
        })) as DeviceAdminDetailWithDeployment[];

        // Filter out devices that are active in deployments
        const deploymentsResp = await clientService.api.getAllDeploymentsApiV1AdminDeploymentsGet();
        const deploymentsList = (deploymentsResp && Array.isArray(deploymentsResp.data)) ? deploymentsResp.data : [];
        const activeDeploymentDeviceIds = new Set(deploymentsList.map(d => d && d.device_id).filter(Boolean));
        
        const idleProjectDevices = projectDevices.filter(d => d && !activeDeploymentDeviceIds.has(d.device_id));
        setDevices(idleProjectDevices);
      } catch (error) {
        console.error('Failed to fetch project devices:', error);
        setDevices([]);
      }
    };

    if (open && !isEdit) {
      fetchProjectDevices();
    }
  }, [formData.project_id, open, isEdit]);

  useEffect(() => {
    if (formData.project_id && !isEdit) {
      fetchSites(formData.project_id);
    }
  }, [formData.project_id, isEdit]);

  const fetchSites = async (projectId: string) => {
    try {
      const response = await clientService.api.readSitesForProjectApiV1ProjectsProjectIdSitesGet(projectId);
      setSites(response.data);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  };

  const fetchLocations = async (siteId: string) => {
    try {
      const response = await clientService.api.getSiteDetailsAdminApiV1AdminSitesSiteIdGet(siteId);
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  useEffect(() => {
    if (formData.site_id) {
      fetchLocations(formData.site_id);
    } else {
      setLocations([]);
    }
  }, [formData.site_id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.deployment_name) newErrors.deployment_name = 'Required';
    if (!isEdit) {
      if (!formData.project_id) newErrors.project_id = 'Required';
      if (!formData.site_id) newErrors.site_id = 'Required';
      if (!formData.location_id) newErrors.location_id = 'Required';
      if (!formData.device_id) newErrors.device_id = 'Required';
    }
    if (!formData.start_date) newErrors.start_date = 'Required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      if (isEdit && deployment) {
        const payload: DeploymentUpdate = {
          deployment_name: formData.deployment_name,
          start_date: formData.start_date,
          end_date: formData.end_date || null,
          data_upload_status: formData.data_upload_status,
          status: formData.status
        };
        const response = await clientService.api.updateApiDeploymentApiV1AdminDeploymentsDeploymentIdPut(deployment.deployment_id, payload);
        onSuccess(response.data as DeploymentAdminListDetail);
        showSnackbar({ message: 'Deployment updated successfully!', type: SnackbarType.SUCCESS });
      } else {
        const payload: DeploymentCreate = {
          deployment_name: formData.deployment_name,
          device_id: formData.device_id,
          location_id: formData.location_id,
          start_date: formData.start_date,
          end_date: formData.end_date || null,
          data_upload_status: formData.data_upload_status,
          status: formData.status,
          site_id: formData.site_id,
          project_id: formData.project_id
        };
        const response = await clientService.api.createApiDeploymentApiV1AdminDeploymentsPost(payload);
        onSuccess(response.data as DeploymentAdminListDetail);
        showSnackbar({ message: 'Deployment created successfully!', type: SnackbarType.SUCCESS });
      }
      onClose();
    } catch (error) {
      console.error('Failed to save deployment:', error);
      showSnackbar({ message: `Failed to ${isEdit ? 'update' : 'create'} deployment`, type: SnackbarType.ERROR });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: '#111',
          color: '#FFF',
          borderRadius: '16px',
          maxWidth: isEdit ? '800px' : '500px',
          width: '100%',
          border: '1px solid #222',
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, pb: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>{isEdit ? 'Edit Deployment' : 'Create New Deployment'}</Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            {isEdit ? 'Update the information for this deployment.' : 'Fill in the details below to create a new deployment.'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#666' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 3,
          overflowY: 'auto',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        {isEdit && deployment && (
          <Box sx={{ bgcolor: '#050505', border: '1px solid #222', borderRadius: '12px', p: 3, mb: 4 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#555', fontWeight: 700, textTransform: 'uppercase' }}>Device</Typography>
                  <Typography variant="body1" sx={{ color: '#DDD', fontWeight: 600 }}>{deployment.deployment_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#555', fontWeight: 700, textTransform: 'uppercase' }}>Created At</Typography>
                  <Typography variant="body1" sx={{ color: '#DDD', fontWeight: 600 }}>{formatDate(deployment.created_at)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#555', fontWeight: 700, textTransform: 'uppercase' }}>Location</Typography>
                  <Typography variant="body1" sx={{ color: '#DDD', fontWeight: 600 }}>{deployment.site_name}</Typography>
                </Box>
                <Grid container>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#555', fontWeight: 700, textTransform: 'uppercase' }}>Total Files</Typography>
                    <Typography variant="body1" sx={{ color: '#DDD', fontWeight: 600 }}>{deployment.file_count || 0}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#555', fontWeight: 700, textTransform: 'uppercase' }}>Analyzed Files</Typography>
                    <Typography variant="body1" sx={{ color: '#DDD', fontWeight: 600 }}>{deployment.inference_count || 0}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={{ mb: 4 }}>
          <TextField
            fullWidth
            label="Deployment Name"
            name="deployment_name"
            placeholder="WG_AG_2026_Monitoring"
            value={formData.deployment_name}
            onChange={handleChange}
            error={!!errors.deployment_name}
            helperText={errors.deployment_name}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#151515',
                color: '#FFF',
                borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
              '& .MuiInputLabel-root': { color: '#666' },
            }}
          />
          {!isEdit && <Typography variant="caption" sx={{ color: '#444', mt: 0.5 }}>Enter a unique name for this deployment.</Typography>}
        </Box>


        {!isEdit && (
          <>
            <Box sx={{ mb: 4 }}>
              <TextField
                select
                fullWidth
                label="Project"
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
                error={!!errors.project_id}
                helperText={errors.project_id}
                disabled={!!initialProjectId}

                SelectProps={{
                  IconComponent: KeyboardArrowDownIcon,


                  MenuProps: {
                    PaperProps: {
                      sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' }
                    }
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#444' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                  },
                }}
              >
                {projects.map((p) => <MenuItem key={p.project_id} value={p.project_id}>{p.project_name}</MenuItem>)}
              </TextField>
            </Box>

            <Box sx={{ mb: 4 }}>
              <TextField
                select
                fullWidth
                label="Site"
                name="site_id"
                value={formData.site_id}
                onChange={handleChange}
                error={!!errors.site_id}
                helperText={errors.site_id}
                disabled={!formData.project_id || !!initialSiteId}

                SelectProps={{
                  IconComponent: KeyboardArrowDownIcon,


                  MenuProps: {
                    PaperProps: {
                      sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' }
                    }
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#444' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                  },
                }}
              >
                {sites.map((s) => <MenuItem key={s.site_id} value={s.site_id}>{s.site_name}</MenuItem>)}
              </TextField>
            </Box>

            <Box sx={{ mb: 4 }}>
              <TextField
                select
                fullWidth
                label="Location"
                name="location_id"
                value={formData.location_id}
                onChange={handleChange}
                error={!!errors.location_id}
                helperText={errors.location_id}
                disabled={!formData.site_id}

                SelectProps={{
                  IconComponent: KeyboardArrowDownIcon,


                  MenuProps: {
                    PaperProps: {
                      sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' }
                    }
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#444' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                  },
                }}
              >
                {locations.map((l) => <MenuItem key={l.location_id} value={l.location_id}>{l.location_name}</MenuItem>)}
              </TextField>
            </Box>
          </>
        )}

        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>Deployment Period *</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#444', display: 'block', mb: 0.5 }}>Start Date</Typography>
              <TextField
                fullWidth type="date" name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                error={!!errors.start_date}
                helperText={errors.start_date}

                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: '#444', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#444' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                    '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ color: '#444', display: 'block', mb: 0.5 }}>End Date</Typography>
              <TextField
                fullWidth type="date" name="end_date"
                value={formData.end_date}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarTodayIcon sx={{ color: '#444', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                    '& fieldset': { borderColor: '#333' },
                    '&:hover fieldset': { borderColor: '#444' },
                    '&.Mui-focused fieldset': { borderColor: '#10B981' },
                    '& input::-webkit-calendar-picker-indicator': { filter: 'invert(1)' },
                  },
                }}
              />
            </Grid>
          </Grid>
          <Typography variant="caption" sx={{ color: '#444', mt: 0.5 }}>Select the start and end date for this deployment.</Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#AAA' }}>Status</Typography>
          <TextField
            select
            fullWidth
            name="status"
            value={formData.status}
            onChange={handleChange}
            SelectProps={{
              IconComponent: KeyboardArrowDownIcon,
              MenuProps: {
                PaperProps: {
                  sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' }
                }
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#444' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
            }}
          >
            {Object.values(DeploymentStatus).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Box>

        {!isEdit && (
          <Box sx={{ mb: 4 }}>
            <TextField
              select
              fullWidth
              label="Device"
              name="device_id"
              value={formData.device_id}
              onChange={handleChange}
              error={!!errors.device_id}
              helperText={errors.device_id}

              SelectProps={{
                IconComponent: KeyboardArrowDownIcon,


                MenuProps: {
                  PaperProps: {
                    sx: { bgcolor: '#1A1A1A', color: '#FFF', border: '1px solid #333' }
                  }
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#151515', color: '#FFF', borderRadius: '8px',
                  '& fieldset': { borderColor: '#333' },
                  '&:hover fieldset': { borderColor: '#444' },
                  '&.Mui-focused fieldset': { borderColor: '#10B981' },
                },
              }}
            >
              {devices.map((d) => (
                <MenuItem key={d.device_id} value={d.device_id}>
                  {d.human_readable_id} - {d.name}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" sx={{ color: '#444', mt: 0.5 }}>Select the device to be used for this deployment.</Typography>
          </Box>
        )}

        {isEdit ? (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.data_upload_status}
                  onChange={handleChange}
                  name="data_upload_status"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10B981' },
                  }}
                />
              }
              label={<Typography variant="body2" sx={{ color: '#FFF', fontWeight: 600 }}>Data Upload Status</Typography>}
            />
          </Box>
        ) : (
          <Box sx={{
            bgcolor: 'rgba(59, 130, 246, 0.05)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '8px',
            p: 2, display: 'flex', gap: 1.5, alignItems: 'flex-start'
          }}>
            <InfoIcon sx={{ color: '#3B82F6', fontSize: 20, mt: 0.3 }} />
            <Box>
              <Typography variant="body2" sx={{ color: '#3B82F6', fontWeight: 600 }}>Only available (idle) devices are shown.</Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Devices already assigned to active deployments are not selectable.</Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, borderTop: '1px solid #222' }}>
        <Button
          onClick={onClose}
          sx={{
            color: '#AAA', textTransform: 'none', fontWeight: 600,
            '&:hover': { color: '#FFF', bgcolor: 'transparent' }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : (isEdit ? <SaveIcon /> : <AddIcon />)}
          sx={{
            bgcolor: '#10B981', color: '#000', px: 4, borderRadius: '8px',
            textTransform: 'none', fontWeight: 700,
            '&:hover': { bgcolor: '#059669' }
          }}
        >
          {isEdit ? 'Save Changes' : 'Create Deployment'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default DeploymentDialog;